import { useQuery } from '@apollo/client';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { EditPostReplyDraftFormRender } from '../../../components/PostReplyDraft/EditPostReplyDraftFormRender';
import PostReplyDraftComponent from '../../../components/PostReplyDraft/PostReplyDraft.component';
import SimpleBackdrop from '../../../components/UI/SimpleBackdrop';
import { SimpleDialog } from '../../../components/UI/SimpleDialog';
import { POST_REPLY_DRAFT } from '../../../constants/postReplyDraft.constants';
import { GET_POST_BY_ID_QUERY } from '../../../graphql/resolvers/postQueries/getPostById.gql';
import { useFeedPostSubscriptions } from '../../../hooks/useFeedPostSubscriptions';
import { useReplyDrafts } from '../../../hooks/useReplyDrafts';
import type { FeedPost } from '../../../interfaces/feed.interface';
import type { PostDraftInterface } from '../../../interfaces/post.interface';
import type { UserState } from '../../../interfaces/user.interface';
import { postReplyDraftSlice } from '../../../store/postReplyDraft.store';
import './post-reply-draft.scss';

export default function PostReplyDraft() {
  const params = useParams();
  const { postReplyDraftId } = params;
  const dispatch = useDispatch();
  const { user } = useSelector((state: UserState) => state.userStore);
  const username = user?.username;
  // TRIL-106: drive directly from the unified replyDraftsStore via the
  // useReplyDrafts hook, not from the legacy postReplyDraftsStore which
  // only gets warmed when the user has previously visited the feed.
  // Without this, deep-linking from the post-detail "go to reply-in-the-
  // works" link would land on an empty page until the legacy store was
  // populated by another route.
  const { drafts: replyDrafts, loading: replyDraftsLoading } = useReplyDrafts(
    user?.id,
  );
  const { postReplyDraft, refreshPostReplyDraft } = useSelector(
    (state: any) => state.postReplyDraftStore,
  );
  const { id } = user;
  const [postReplyDraftLocalState, setPostReplyDraftLocalState] = useState<
    PostDraftInterface | Record<any, any>
  >({});
  const [parentPost, setParentPost] = useState<FeedPost>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    GET_POST_REPLY_DRAFT_STORE_ACTION,
    IS_EDITING_POST_REPLY_DRAFT_STORE_ACTION,
    REFRESH_POST_REPLY_DRAFT_STORE_ACTION,
  } = postReplyDraftSlice.actions;

  // TRIL-103: while the user is composing a reply on this route, keep the
  // WS connection subscribed to the parent post so INTERACTION_UPDATED /
  // STORY_SCORE_UPDATED / new_replies broadcasts continue to arrive. The
  // post-detail page (/post/:id) had this subscription on mount; navigating
  // here would unsubscribe it, leaving the user blind to like/bookmark
  // events for the post they were replying to. Both the unified-hook path
  // and the singleton fallback are gated on `id === postReplyDraftId` so
  // a stale singleton from a prior reply-draft navigation can't subscribe
  // us to the wrong parent post (including on the not-found path, where
  // the unified hook resolved empty but the singleton still holds the
  // previous draft).
  const parentPostIdForSubscription = (() => {
    const fromHook = (
      replyDrafts.find(
        (d) => (d as PostDraftInterface).id === postReplyDraftId,
      ) as PostDraftInterface | undefined
    )?.parentPostId;
    if (fromHook) return fromHook;
    const singleton = postReplyDraft as PostDraftInterface | undefined;
    if (
      singleton &&
      singleton.id === postReplyDraftId &&
      singleton.parentPostId
    ) {
      return singleton.parentPostId;
    }
    return '';
  })();
  useFeedPostSubscriptions(
    parentPostIdForSubscription ? [parentPostIdForSubscription] : [],
  );

  // TRIL-194: read the parent post from the Apollo cache instead of from
  // feedStore. The post is normalized as Post:{id}; useQuery returns it
  // immediately if the user came from the feed (cache populated), and
  // fires a single network fetch if they deep-linked here.
  const { data: parentPostData } = useQuery(GET_POST_BY_ID_QUERY, {
    variables: { postId: parentPostIdForSubscription },
    skip: !parentPostIdForSubscription,
  });
  const parentPostFromCache = parentPostData?.getPostById as
    | FeedPost
    | undefined;

  useEffect(() => {
    if (!postReplyDraftId || !replyDrafts.length) return;
    const replyDraft = replyDrafts.find(
      (d) => (d as PostDraftInterface).id === postReplyDraftId,
    );
    if (!replyDraft) return;
    dispatch(GET_POST_REPLY_DRAFT_STORE_ACTION(replyDraft));
    setPostReplyDraftLocalState(replyDraft as PostDraftInterface);
    setParentPost(parentPostFromCache);
  }, [
    postReplyDraftId,
    replyDrafts,
    parentPostFromCache,
    dispatch,
    GET_POST_REPLY_DRAFT_STORE_ACTION,
  ]);

  useEffect(() => {
    if (refreshPostReplyDraft) {
      setPostReplyDraftLocalState(postReplyDraft);
      dispatch(REFRESH_POST_REPLY_DRAFT_STORE_ACTION(false));
    }
  }, [refreshPostReplyDraft, postReplyDraft]);

  const handleEditDraft = (event: React.SyntheticEvent, id: string) => {
    setIsModalOpen(true);
    dispatch(IS_EDITING_POST_REPLY_DRAFT_STORE_ACTION(true));
  };

  const handleClose = () => {
    dispatch(IS_EDITING_POST_REPLY_DRAFT_STORE_ACTION(false));
    setIsModalOpen(false);
  };

  // TRIL-106: gate the render on whether the singleton store has been
  // hydrated for THIS URL's draft id. The store's initial state is a
  // truthy empty draft (id: ''), so a `!postReplyDraft` check would
  // never fire and the success path would render with empty data.
  const isDraftHydratedForUrl =
    Boolean(postReplyDraft) && postReplyDraft.id === postReplyDraftId;
  const resolvedDraftFromHook = postReplyDraftId
    ? replyDrafts.find((d) => (d as PostDraftInterface).id === postReplyDraftId)
    : undefined;

  if (!isDraftHydratedForUrl) {
    // Query has fully resolved and the hook does not have this draft —
    // either the URL is stale or the draft was deleted/published.
    if (!replyDraftsLoading && !resolvedDraftFromHook) {
      return (
        <div className="POST__REPLY__DRAFT POST__REPLY__DRAFT--missing">
          <p>
            This reply draft was not found. It may have been published or
            deleted.
          </p>
        </div>
      );
    }
    // Otherwise the hook either is still in flight, or has the draft and
    // we're about to dispatch it into the singleton store on the next
    // tick. Show a clean loading state in both cases.
    return <SimpleBackdrop open={true} />;
  }

  const draftPostRender = postReplyDraft && (
    <>
      <div className="POST__REPLY__DRAFT">
        <div className="POST__REPLY__DRAFT__CONTAINER">
          <PostReplyDraftComponent
            parentPost={parentPost as FeedPost}
            username={username as string}
            userId={id as string}
            onEditDraft={() =>
              handleEditDraft(
                {} as React.SyntheticEvent,
                postReplyDraftId as string,
              )
            }
          />
        </div>
      </div>
      <SimpleDialog
        fullWidth
        className="Simple-Dialog-From-Text-Editor"
        closeModalLabel={POST_REPLY_DRAFT.CANCEL}
        dialogBody={<EditPostReplyDraftFormRender handleClose={handleClose} />}
        dialogTitle={POST_REPLY_DRAFT.EDIT_POST_REPLY_DRAFT}
        maxWidth="lg"
        open={isModalOpen}
        textEditor={true}
        onClose={handleClose}
      />
    </>
  );
  return draftPostRender;
}
